import { formatPhoneNumber } from '../../../../utils/formatPhoneNumber';
import React, { useState, useEffect } from 'react';
import Modal from '../../../../components/shareable/modal';
import AdditionalLocations from './AdditionalLocations';
import './style.css';

const FullUpdateModal = ({ isOpen, onClose, onSave, Update = "Update Customer", initialData = {} }) => {
  const [formData, setFormData] = useState({ additional_locations: [] });
  const [errors, setErrors] = useState({});
  const [locationErrors, setLocationErrors] = useState([]);
  
  useEffect(() => {
    if (initialData) {
      setFormData({
        customer: initialData.customer || '',
        primary_contact: initialData.primary_contact || '',
        address: initialData.address || '',
        address2: initialData.address2 || '',
        city: initialData.city || '',
        state: initialData.state || '',
        zip: initialData.zip || '',
        standard_discount: initialData.standard_discount || '',
        title: initialData.title || '',
        phone: initialData.phone || '',
        email: initialData.email || '',
        additional_locations: initialData.additional_locations || []
      });
      
      // Initialize location errors based on existing locations
      if (initialData.additional_locations && initialData.additional_locations.length > 0) {
        setLocationErrors(initialData.additional_locations.map(() => false));
      } else {
        setLocationErrors([]);
      }
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
  
    if (name === 'phone') {
      // Remove all non-digit characters
      const cleanedValue = value.replace(/\D/g, '');
      // Limit to 10 digits
      const limitedValue = cleanedValue.slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: limitedValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) { setErrors(prev => ({ ...prev, [name]: '' })); }
  };

  // Handle changes for additional locations
  const handleLocationsChange = (updatedLocations) => { setFormData(prev => ({ ...prev, additional_locations: updatedLocations })); };

  // Handle errors for additional locations
  const handleLocationErrorsChange = (updatedErrors) => { setLocationErrors(updatedErrors); };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customer.trim()) { newErrors.customer = 'Customer name is required'; }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Validate all locations have a location name
    const newLocationErrors = [...locationErrors];
    let hasLocationError = false;
    
    formData.additional_locations.forEach((location, index) => {
      if (!location.location.trim()) {
        newLocationErrors[index] = true;
        hasLocationError = true;
      } else {
        newLocationErrors[index] = false;
      }
    });
    
    if (hasLocationError) { setLocationErrors(newLocationErrors); }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !hasLocationError;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) { return; }
    
    onSave(formData);
    onClose();
  };

  // Define the order of fields to match your schema
  const orderedFields = [
    'customer', 'primary_contact', 'email',
    'phone', 'address', 'address2',
    'city', 'state', 'zip',
    'title', 'standard_discount'
  ];

  // Group fields into rows of 3
  const fieldGroups = [];
  for (let i = 0; i < orderedFields.length; i += 3) {
    fieldGroups.push(orderedFields.slice(i, i + 3));
  }

  return (
    <Modal isOpen={isOpen} width='3xl' onClose={onClose}>
      <div className="flex flex-col h-full">
        <h2 className="text-lg font-semibold mb-4">{Update}</h2>
        
        {/* Scrollable form content */}
        <div className="flex-grow overflow-y-auto max-h-96">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {fieldGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="grid grid-cols-3 gap-4">
                  {group.map((field) => (
                    <div key={field}>
                      <label className="block text-sm font-medium mb-1 capitalize">
                        {field.replace(/_/g, ' ')}
                        {(field === 'customer' || field === 'email') && <span className="text-red-500">*</span>}
                      </label>
                      {field === "phone" ? (
                        <input
                          type="text"
                          name={field}
                          value={formatPhoneNumber(formData[field])}
                          onChange={handleChange}
                          className={`w-full p-1.5 text-sm border rounded ${errors[field] ? 'border-red-500' : ''}`}
                          placeholder="(123) 456-7890"
                        />
                      ) : (
                        <input
                          type={field === 'email' ? 'email' : 'text'}
                          name={field}
                          value={formData[field]}
                          onChange={handleChange}
                          className={`w-full p-1.5 text-sm border rounded ${errors[field] ? 'border-red-500' : ''}`}
                          placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                        />
                      )}
                      {errors[field] && <p className="mt-1 text-xs text-red-500">{errors[field]}</p>}
                    </div>
                  ))}
                </div>
              ))}
              
              {/* Additional Locations Section */}
              <AdditionalLocations locations={formData.additional_locations} onLocationsChange={handleLocationsChange} locationErrors={locationErrors} onLocationErrorsChange={handleLocationErrorsChange} />
            </div>
          </form>
        </div>
        
        <div className="flex justify-end space-x-2 mt-4 pt-4 border-t">
          <button type="button" onClick={onClose} className="px-6 py-1.5 text-sm bg-gray-300 rounded hover:bg-gray-400" > Cancel </button>
          <button type="submit" onClick={handleSubmit} className="px-6 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700" > Save </button>
        </div>
      </div>
    </Modal>
  );
};

export default FullUpdateModal;
